import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'src/utils/axios';

const normalizeListResponse = response => {
  let data = response.data;

  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (error) {
      return [];
    }
  }

  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') return Object.values(data);
  return [];
};

export const getActivePaymentTypes = createAsyncThunk(
  'manualDiscount/getActivePaymentTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/GetActivePaymentTypes');
      return normalizeListResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active payment types');
    }
  }
);

export const getActiveWarrantors = createAsyncThunk(
  'manualDiscount/getActiveWarrantors',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/getActiveWarrantors');
      return normalizeListResponse(response);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active warrantors');
    }
  }
);

export const applyManualDiscount = createAsyncThunk(
  'manualDiscount/applyManualDiscount',
  async (params, { rejectWithValue }) => {
    try {
      const response = await axios.post('/ApplyManualDiscount', {
        WorkOrderId: params.workOrderId,
        NewLabourTotal: params.newLabourTotal,
        NewSparePartTotal: params.newSparePartTotal,
        NewTransportTotal: params.newTransportTotal,
        NewExtraTotal: params.newExtraTotal,
        EmployeeId: params.employeeId,
        Warrantor: params.warrantor ? parseInt(params.warrantor) : null,
        PaymentTypeId: params.paymentTypeId ? parseInt(params.paymentTypeId) : null,
        PaymentTypeDetail: params.paymentTypeDetail || null,
        Details: params.details || null
      });
      let responseData = response.data;
      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = { Message: responseData };
        }
      }
      if (responseData?.Success === false || responseData?.success === false) {
        return rejectWithValue(responseData.Message || responseData.message || 'Failed to apply discount');
      }
      return responseData;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data?.Message || error.message || 'Failed to apply discount'
      );
    }
  }
);

const manualDiscountSlice = createSlice({
  name: 'manualDiscount',
  initialState: {
    paymentTypeList: [],
    warrantorList: [],
    listLoading: false,
    applyingDiscount: false,
    error: ''
  },
  reducers: {
    clearManualDiscountError(state) {
      state.error = '';
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getActivePaymentTypes.pending, state => {
        state.listLoading = true;
        state.error = '';
      })
      .addCase(getActivePaymentTypes.fulfilled, (state, action) => {
        state.paymentTypeList = action.payload;
        state.listLoading = false;
      })
      .addCase(getActivePaymentTypes.rejected, (state, action) => {
        state.paymentTypeList = [];
        state.listLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(getActiveWarrantors.pending, state => {
        state.listLoading = true;
        state.error = '';
      })
      .addCase(getActiveWarrantors.fulfilled, (state, action) => {
        state.warrantorList = action.payload;
        state.listLoading = false;
      })
      .addCase(getActiveWarrantors.rejected, (state, action) => {
        state.warrantorList = [];
        state.listLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(applyManualDiscount.pending, state => {
        state.applyingDiscount = true;
        state.error = '';
      })
      .addCase(applyManualDiscount.fulfilled, state => {
        state.applyingDiscount = false;
      })
      .addCase(applyManualDiscount.rejected, (state, action) => {
        state.applyingDiscount = false;
        state.error = action.payload || action.error.message;
      });
  }
});

export const { clearManualDiscountError } = manualDiscountSlice.actions;
export default manualDiscountSlice.reducer;
